import { BufferAttribute, BufferGeometry, TorusGeometry, Vector3 } from 'three';
import type { Rng } from '@cloudpearl/core';
import { MeshBuilder, box, mixRGB, noise3, rgb, scaleRGB, smoothRock, smoothTube } from './builder';
import type { RGB, TubeRing } from './builder';

function rubble(mb: MeshBuilder, rng: Rng, colors: RGB[], x: number, z: number, spread: number, count: number, size: [number, number]): void {
  for (let k = 0; k < count; k++) {
    const r = rng.range(size[0], size[1]);
    const g = smoothRock(rng, { radius: r, height: r * rng.range(0.7, 1.1), colors, detail: 3, roughness: 0.18 });
    g.rotateY(rng.float() * Math.PI * 2);
    g.translate(x + rng.range(-spread, spread), -0.1, z + rng.range(-spread, spread));
    mb.addGeometry(g);
  }
}

/** Weathered fluted column on a plinth, broken top. r≈0.85, h≈6.2. */
export function pillar(rng: Rng, stone: string, dark: string, mb = new MeshBuilder(), x = 0, h = rng.range(5.4, 6.2)): MeshBuilder {
  const c = rgb(stone);
  const d = rgb(dark);
  box(mb, x, 0.25, 0, 2.0, 0.6, 2.0, mixRGB(c, d, 0.25), rng, 0.08);
  const rings: TubeRing[] = [];
  const n = 10;
  for (let i = 0; i <= n; i++) rings.push({ p: new Vector3(x + (rng.float() - 0.5) * 0.04, 0.5 + (i / n) * (h - 0.6), 0), r: 0.8 * (1 - (i / n) * 0.07) });
  smoothTube(mb, rings, 32, { color: c, colorEnd: mixRGB(c, d, 0.15), ribs: 16, ribDepth: 0.035, jitter: 0.03, rootAO: 0.35, capEnd: true, capLift: 0.1, seed: rng.float() * 40 });
  // Broken crown: a cracked chunk sitting slightly askew.
  const crown = smoothRock(rng, { radius: 0.78, height: 0.7, colors: [c, mixRGB(c, d, 0.2)], detail: 3, roughness: 0.25, squash: 0.55, cleaves: 5 });
  crown.rotateZ((rng.float() - 0.5) * 0.25);
  crown.translate(x, h - 0.25, 0);
  mb.addGeometry(crown);
  return mb;
}

/** Ancient stone gate: columns at ±4.6 (r≈0.95, h 7.4) + lintel 5.6–7.6. */
export function gate(rng: Rng, stone: string, dark: string): BufferGeometry {
  const mb = new MeshBuilder();
  const c = rgb(stone);
  const d = rgb(dark);
  for (const x of [-4.6, 4.6]) {
    box(mb, x, 0.3, 0, 2.2, 0.6, 2.2, mixRGB(c, d, 0.3), rng, 0.08);
    const rings: TubeRing[] = [];
    for (let i = 0; i <= 8; i++) rings.push({ p: new Vector3(x, 0.6 + (i / 8) * 5.1, 0), r: 0.88 });
    smoothTube(mb, rings, 32, { color: c, ribs: 16, ribDepth: 0.03, jitter: 0.025, rootAO: 0.3, seed: x });
    box(mb, x, 5.95, 0, 2.1, 0.5, 2.1, c, rng, 0.06);
  }
  box(mb, 0, 6.85, 0, 11.6, 1.5, 1.7, c, rng, 0.18);
  box(mb, rng.range(-1, 1), 7.75, 0, 3.0, 0.3, 1.3, mixRGB(c, d, 0.2), rng, 0.06);
  return mb.build();
}

/** Ruined wall: broken stubs, a column stump and a toppled drum. Collider box ±3.6 × ±0.9, h 3.2. */
export function ruin(rng: Rng, stone: string, dark: string): BufferGeometry {
  const mb = new MeshBuilder();
  const c = rgb(stone);
  const d = rgb(dark);
  const n = rng.int(3, 4);
  const span = 7.0;
  for (let i = 0; i < n; i++) {
    const h = rng.range(1.0, 3.2);
    const w = span / n - 0.15;
    box(mb, -span / 2 + (i + 0.5) * (span / n), h / 2, rng.range(-0.15, 0.15), w, h, 1.5, mixRGB(c, d, rng.float() * 0.3), rng, 0.2);
  }
  pillar(rng, stone, dark, mb, span / 2 + 0.6, rng.range(1.6, 3.0));
  const drumRings: TubeRing[] = [];
  for (let i = 0; i <= 3; i++) drumRings.push({ p: new Vector3(-2.2 + i * 0.5, 0.7, -1.7 - i * 0.1), r: 0.7 });
  smoothTube(mb, drumRings, 24, { color: c, ribs: 16, ribDepth: 0.03, capEnd: true, capStart: true, capLift: 0.02 });
  rubble(mb, rng, [c, d], 0, 1.4, 3.2, 4, [0.25, 0.55]);
  return mb.build();
}

/** Sandstone arch: legs at ±6.4 (r≈1.9) up to a span occupying y 10.2–15. */
export function arch(rng: Rng, rock: string, dark: string): BufferGeometry {
  const mb = new MeshBuilder();
  const c = rgb(rock);
  const d = rgb(dark);
  const rings: TubeRing[] = [];
  const n = 40;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const ang = Math.PI * (1 - t);
    const ca = Math.cos(ang);
    const sa = Math.sin(ang);
    const x = Math.sign(ca) * Math.pow(Math.abs(ca), 0.75) * 6.4;
    const y = Math.pow(sa, 0.5) * 12.6;
    const legness = 1 - Math.pow(sa, 4);
    const r = 1.75 + legness * 0.5 + (noise3(t * 5, 1.3, 0.2) - 0.5) * 0.6;
    rings.push({ p: new Vector3(x, y, (noise3(t * 3, 7, 1) - 0.5) * 0.6), r: r * (i < 2 || i > n - 2 ? 1.18 : 1) });
  }
  rings[0]!.p.y = -1.0;
  rings[n]!.p.y = -1.0;
  smoothTube(mb, rings, 20, { color: c, colorEnd: mixRGB(c, d, 0.1), jitter: 0.16, seed: rng.float() * 20, uvDensity: 0.4 });
  for (const x of [-6.4, 6.4]) rubble(mb, rng, [c, d], x, 0, 2.6, 3, [0.6, 1.2]);
  return mb.build();
}

/** Hoodoo: an eroded, pinched sandstone column, wind-rounded at the top. r≈1.45, h≈11. */
export function spire(rng: Rng, rock: string, dark: string): BufferGeometry {
  const mb = new MeshBuilder();
  const c = rgb(rock);
  const d = rgb(dark);
  const rings: TubeRing[] = [{ p: new Vector3(0, -0.8, 0), r: 1.6 }];
  const n = 26;
  const h = rng.range(10, 11);
  const freq = rng.range(2.2, 3.2);
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const pinch = 0.74 + 0.26 * Math.abs(Math.sin(t * Math.PI * freq));
    rings.push({ p: new Vector3((noise3(t * 4, 2, 3) - 0.5) * 0.4, t * h, (noise3(t * 4, 9, 1) - 0.5) * 0.4), r: 1.42 * pinch * (1 - t * 0.25) });
  }
  // One sandstone column, rounded off at the top by the wind (no dark caprock).
  smoothTube(mb, rings, 22, { color: c, colorEnd: mixRGB(c, d, 0.1), jitter: 0.1, rootAO: 0.3, seed: rng.float() * 30, uvDensity: 0.5, capEnd: true, capLift: 0.35 });
  return mb.build();
}

/** Distant mesa / butte. Radius ≈ 40, height ≈ 46. Stepped talus slopes and sheer upper walls. */
export function mesa(rng: Rng, rock: string, dark: string): BufferGeometry {
  const mb = new MeshBuilder();
  const c = rgb(rock);
  const d = rgb(dark);
  const tiers: [number, number][] = [
    [-14, 50],
    [0, 45],
    [6, 39],
    [12, 34],
    [16, 32],
    [36, 31],
    [42, 29.5],
    [45, 28],
    [46.2, 26.5],
  ];
  const rings: TubeRing[] = tiers.map(([y, r]) => ({ p: new Vector3(0, y, 0), r }));
  smoothTube(mb, rings, 48, { color: mixRGB(c, d, 0.2), colorEnd: c, jitter: 0.12, ribs: 23, ribDepth: 0.025, capEnd: true, capLift: 0.04, seed: rng.float() * 10, uvDensity: 0.1 });
  return mb.build();
}

export interface HouseParts {
  walls: BufferGeometry;
  roof: BufferGeometry;
}

/** Mediterranean cottage: plaster walls, dark windows, terracotta roof. Collider box ±2.6 × ±2.5. */
export function house(rng: Rng, roofHex: string): HouseParts {
  const plasters = ['#f3e2c0', '#f2d07a', '#f6efe4', '#e9b98f', '#efd9a8'];
  const walls = new MeshBuilder();
  const roof = new MeshBuilder();
  const w = rng.range(4.4, 5.0);
  const d = rng.range(4.2, 4.8);
  const h = rng.range(3.8, 5.6);
  const col = rgb(rng.pick(plasters));
  box(walls, 0, h / 2, 0, w, h, d, col, rng, 0.04);
  box(walls, 0, 0.2, 0, w + 0.3, 0.4, d + 0.3, mixRGB(col, rgb('#8a7a6a'), 0.4));
  const glass = rgb('#2c3550');
  const shutter = rgb(rng.pick(['#5f8f6a', '#4f7aa6', '#9a5a3a']));
  const floors = h > 4.6 ? 2 : 1;
  for (let f = 0; f < floors; f++) {
    const y = 1.5 + f * 2.1 + (floors === 1 ? 0.6 : 0);
    for (const side of [-1, 1]) {
      const x = side * w * 0.24;
      box(walls, x, y, d / 2 + 0.02, 0.7, 0.95, 0.08, glass);
      box(walls, x - 0.55, y, d / 2 + 0.04, 0.3, 1.0, 0.06, shutter);
      box(walls, x + 0.55, y, d / 2 + 0.04, 0.3, 1.0, 0.06, shutter);
      box(walls, x, y - 0.55, d / 2 + 0.06, 0.95, 0.1, 0.18, mixRGB(col, rgb('#ffffff'), 0.3));
      box(walls, w / 2 + 0.02, y, side * d * 0.22, 0.08, 0.9, 0.65, glass);
    }
  }
  box(walls, 0, 1.0, d / 2 + 0.03, 0.95, 2.0, 0.08, rgb('#6b4630'));
  const rc = rgb(roofHex);
  const oh = 0.35;
  const ridge = h + rng.range(1.2, 1.8);
  const a = new Vector3(-w / 2 - oh, h - 0.1, -d / 2 - oh);
  const b = new Vector3(w / 2 + oh, h - 0.1, -d / 2 - oh);
  const e = new Vector3(w / 2 + oh, h - 0.1, d / 2 + oh);
  const f = new Vector3(-w / 2 - oh, h - 0.1, d / 2 + oh);
  const r0 = new Vector3(-w / 2 - oh, ridge, 0);
  const r1 = new Vector3(w / 2 + oh, ridge, 0);
  roof.flatQuad(f, e, r1, r0, rc);
  roof.flatQuad(b, a, r0, r1, scaleRGB(rc, 0.92));
  // Gables in wall colour.
  const gn = new Vector3(-1, 0, 0);
  let i = walls.vertex(new Vector3(-w / 2, h, d / 2), gn, col);
  walls.vertex(new Vector3(-w / 2, ridge - 0.1, 0), gn, col);
  walls.vertex(new Vector3(-w / 2, h, -d / 2), gn, col);
  walls.tri(i, i + 1, i + 2);
  gn.set(1, 0, 0);
  i = walls.vertex(new Vector3(w / 2, h, -d / 2), gn, col);
  walls.vertex(new Vector3(w / 2, ridge - 0.1, 0), gn, col);
  walls.vertex(new Vector3(w / 2, h, d / 2), gn, col);
  walls.tri(i, i + 1, i + 2);
  box(walls, w * 0.25, ridge - 0.2, -d * 0.18, 0.55, 1.6, 0.55, mixRGB(col, rgb('#8a6a5a'), 0.3), rng, 0.03);
  const rg = roof.build();
  const uv = rg.getAttribute('uv') as BufferAttribute;
  const p = rg.getAttribute('position');
  for (let k = 0; k < p.count; k++) uv.setXY(k, (p.getX(k) + w) * 0.5, p.getY(k) - h);
  return { walls: walls.build(), roof: rg };
}

/** Waterfall ribbon flowing out along +X and down. uv.x across, uv.y down the fall. */
export function waterfallRibbon(height: number, width: number): BufferGeometry {
  const segs = 32;
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= segs; i++) {
    const v = i / segs;
    const y = -Math.pow(v, 1.15) * height;
    const x = 0.3 + Math.sqrt(v) * height * 0.07;
    const w = width * (0.8 + v * 0.9);
    for (let j = 0; j <= 6; j++) {
      const u = j / 6;
      pos.push(x + Math.sin(u * Math.PI) * 0.35, y, (u - 0.5) * w);
      uv.push(u, v);
    }
  }
  for (let i = 0; i < segs; i++) {
    for (let j = 0; j < 6; j++) {
      const a = i * 7 + j;
      idx.push(a, a + 7, a + 1, a + 1, a + 7, a + 8);
    }
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('uv', new BufferAttribute(new Float32Array(uv), 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  g.computeBoundingSphere();
  return g;
}

/** Wind-gate ring (unit radius, facing ±Z). uv.x runs around the ring. */
export function windGateGeometry(): BufferGeometry {
  const g = new TorusGeometry(1, 0.06, 12, 96);
  g.computeBoundingSphere();
  return g;
}
